import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

let storeAuth = null

export const useStoreReferrals = defineStore('storeReferrals', {
  persist: false,
  state: () => {
    return {
      referralStats: null,
      myReferrals: [],
      validationResult: null,
      validationLoading: false,
      validationError: null,
      redeemLoading: false,
      redeemError: null,
      statsLoading: false,
      referralsLoading: false,
      loaded: false,
      _fetchStatsPromise: null,
      _fetchMyReferralsPromise: null
    }
  },
  actions: {
    init() {
      storeAuth = useStoreAuth()

      if (storeAuth.user) {
        this.fetchReferralStats()
        this.fetchMyReferrals()
      }
    },

    async validateReferralCode(referralCode) {
      if (!referralCode || referralCode.trim() === '') {
        this.validationResult = null
        this.validationError = null
        return null
      }

      this.validationLoading = true
      this.validationError = null

      try {
        const { data, error } = await supabase.functions.invoke(
          'referrals/validate',
          {
            body: {
              referral_code: referralCode.toUpperCase()
            }
          }
        )

        if (error) {
          const errorResponse = await new Response(error.context.body).json()
          this.validationError =
            errorResponse.message || 'Invalid referral code'
          this.validationResult = null
          return null
        }

        this.validationResult = data.data
        this.validationError = null
        return data.data
      } catch (error) {
        console.error('Error validating referral code:', error)
        this.validationError = 'Failed to validate referral code'
        this.validationResult = null
        return null
      } finally {
        this.validationLoading = false
      }
    },

    async redeemReferralCode(referralCode, newUserId) {
      this.redeemLoading = true
      this.redeemError = null

      try {
        const { data, error } = await supabase.functions.invoke(
          'referrals/redeem',
          {
            body: {
              referral_code: referralCode.toUpperCase(),
              new_user_id: newUserId
            }
          }
        )

        if (error) {
          const errorResponse = await new Response(error.context.body).json()
          this.redeemError =
            errorResponse.message || 'Failed to redeem referral code'
          throw new Error(this.redeemError)
        }

        return data.data
      } catch (error) {
        console.error('Error redeeming referral code:', error)
        this.redeemError = error.message || 'Failed to redeem referral code'
        throw error
      } finally {
        this.redeemLoading = false
      }
    },

    async fetchReferralStats() {
      // If a request is already in progress, return the existing promise
      if (this._fetchStatsPromise) {
        return this._fetchStatsPromise
      }

      // Create and store the promise
      this._fetchStatsPromise = this._doFetchReferralStats()

      try {
        return await this._fetchStatsPromise
      } finally {
        // Clear the promise when complete
        this._fetchStatsPromise = null
      }
    },

    async _doFetchReferralStats() {
      // Ensure storeAuth is initialized
      if (!storeAuth) {
        storeAuth = useStoreAuth()
      }

      // Wait for auth to be ready
      const user = await storeAuth.waitForAuth()
      if (!user) return

      this.statsLoading = true

      try {
        const { data, error } =
          await supabase.functions.invoke('referrals/stats')

        if (error) {
          const errorResponse = await new Response(error.context.body).json()
          console.error('Error fetching referral stats:', errorResponse.message)
          return
        }

        this.referralStats = data.data
        this.loaded = true
      } catch (error) {
        console.error('Error fetching referral stats:', error)
      } finally {
        this.statsLoading = false
      }
    },

    async fetchMyReferrals() {
      // If a request is already in progress, return the existing promise
      if (this._fetchMyReferralsPromise) {
        return this._fetchMyReferralsPromise
      }

      // Create and store the promise
      this._fetchMyReferralsPromise = this._doFetchMyReferrals()

      try {
        return await this._fetchMyReferralsPromise
      } finally {
        // Clear the promise when complete
        this._fetchMyReferralsPromise = null
      }
    },

    async _doFetchMyReferrals() {
      // Ensure storeAuth is initialized
      if (!storeAuth) {
        storeAuth = useStoreAuth()
      }

      // Wait for auth to be ready
      const user = await storeAuth.waitForAuth()
      if (!user) return

      this.referralsLoading = true

      try {
        const { data, error } = await supabase.functions.invoke(
          'referrals/my-referrals',
          {
            body: {}
          }
        )

        if (error) {
          const errorResponse = await new Response(error.context.body).json()
          console.error('Error fetching referrals:', errorResponse.message)
          return
        }

        this.myReferrals = data.data.referrals || []
      } catch (error) {
        console.error('Error fetching referrals:', error)
      } finally {
        this.referralsLoading = false
      }
    },

    clearValidation() {
      this.validationResult = null
      this.validationError = null
    },

    clearRedeemError() {
      this.redeemError = null
    },

    // Helper method to copy referral code to clipboard
    async copyReferralCode() {
      if (!this.referralStats?.referral_code) return false

      try {
        await navigator.clipboard.writeText(this.referralStats.referral_code)
        return true
      } catch (error) {
        console.error('Failed to copy referral code:', error)
        return false
      }
    }
  },
  getters: {
    hasReferralCode: (state) => {
      return state.referralStats?.referral_code != null
    },

    referralCode: (state) => {
      return state.referralStats?.referral_code || ''
    },

    totalReferrals: (state) => {
      return state.referralStats?.total_referrals || 0
    },

    redemptionsUsed: (state) => {
      return state.referralStats?.redemptions_used || 0
    },

    remainingUses: (state) => {
      return state.referralStats?.remaining_uses || 0
    },

    referralLimit: (state) => {
      return state.referralStats?.referral_limit || 3
    },

    referralShareUrl: (state) => {
      if (!state.referralStats?.referral_code) return ''
      // Use the actual web domain instead of capacitor://localhost
      const webOrigin =
        import.meta.env.VITE_WEB_URL || 'https://spectra.reviews'
      return `${webOrigin}/invited/${state.referralStats.referral_code}`
    },

    isValidationValid: (state) => {
      return state.validationResult?.valid === true
    },

    validationReferrer: (state) => {
      return state.validationResult?.referrer || null
    },

    validationRemainingUses: (state) => {
      return state.validationResult?.remaining_uses || 0
    }
  }
})
